/**
 * The season fields as they arrive on the wire, both optional because neither
 * key is guaranteed to be present. `CreateRequest`/`MicropostResponse` declare
 * them as required, but `RawMicropost` and `MicropostResponse` are hand-written
 * mirrors of a gogo protobuf contract, so what actually shows up in the JSON is
 * the server's business.
 */
interface RawSeasonFields {
  SeasonID?: number | null
  IsSeasonScoped?: boolean
}

/** The same pair as the app models it: posting season, plus audience scope. */
export interface MicropostSeason {
  /**
   * The season the post was created in. Null only on rows that predate the
   * AB#25433 migration — every post created since carries one, including
   * custom-segment posts, which are never season-*scoped*.
   */
  seasonID: number | null
  /** Whether the audience is restricted to `seasonID`. */
  isSeasonScoped: boolean
}

/**
 * Reads a micropost's season pair off a raw response, collapsing every absence
 * mode to a safe default so no caller has to re-derive them.
 *
 * `SeasonID` and `IsSeasonScoped` are absent when:
 *
 * 1. **The service predates AB#25433** — during a Service-before-Admin deploy
 *    window, or a rollback. Both keys are simply missing.
 * 2. **gogo stripped the zero value** — `IsSeasonScoped` is a plain `bool` on
 *    `MicropostResponse`, so `false` serializes to nothing; `SeasonID` is a
 *    `wktpointer`, so nil serializes to nothing.
 * 3. **The row predates the migration** — `SeasonID` is genuinely `NULL` in the
 *    database and arrives as `null`.
 *
 * All three collapse to "no season, not scoped", which renders as the
 * all-seasons audience — the same thing the list showed before any of this
 * existed. Note that a season-scoped post created between AB#20852 and the
 * AB#25433 migration also lands here: its season was never persisted, so no
 * amount of front-end work can recover it.
 */
export const micropostSeasonFrom = ({
  SeasonID,
  IsSeasonScoped,
}: RawSeasonFields): MicropostSeason => ({
  seasonID: typeof SeasonID === 'number' ? SeasonID : null,
  isSeasonScoped: typeof IsSeasonScoped === 'boolean' ? IsSeasonScoped : false,
})
